import React, { useState, useRef, useEffect } from 'react';
import '../styles/CookieForm.css';
import { useDebug } from '../contexts/DebugContext';

const CookieForm = ({ 
  title, 
  width, 
  length, 
  weightNoIcing, 
  weight, 
  count, 
  onChange, 
  disabled = false, 
  hideTitle = false
 }) => {
  const { isDebugMode, getDebugId } = useDebug();
  const formType = weight !== undefined ? 'ref' : 'new';

  // Component state and handlers
  const handleReset = () => {
    // Reset all fields in this form section
    if (weight !== undefined) {
      // Reference cookie section
      onChange('width', '');
      onChange('length', '');
      onChange('weightNoIcing', '');
      onChange('weight', '');
    } else {
      // Calculate new cookies section
      onChange('width', '');
      onChange('length', '');
      onChange('count', '1');
    }
  };
  const getStep = () => '0.25';
  const getWeightStep = () => '0.1';

  const validateAndFormatValue = (field, value) => {
    // Allow empty fields during typing
    if (value === '' || value === '-') {
      return value;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return '';
    }

    // Always make positive
    const positiveValue = Math.abs(numValue);

    // For count, ensure it's a positive integer (rounded up)
    if (field === 'count') {
      return Math.max(1, Math.ceil(positiveValue)).toString();
    }

    // For other fields, ensure positive number with max 2 decimal places
    return (Math.round(positiveValue * 100) / 100).toString();
  };

  const handleBlur = (field, value) => {
    const formattedValue = validateAndFormatValue(field, value);
    // Only update if there's a change to avoid unnecessary re-renders
    if (formattedValue !== value) {
      onChange(field, formattedValue || '0');
    }
  };

  return (
    <div className="cookie-form">
      {!hideTitle && (
        <div className="form-header">
          <div className="form-title-section">
            <h2>{title}</h2>
          </div>
          <button 
            className="reset-section" 
            onClick={handleReset}
            title={`Reset ${title}`}
          >
            ↺ Reset
          </button>
        </div>
      )}
      <div className="input-group">
        <div className="input-field">
          <label>
            Width: <span className="unit-label">(in)</span>
          </label>
          <input
            type="number"
            value={width}
            onChange={(e) => onChange('width', e.target.value)}
            onBlur={(e) => handleBlur('width', e.target.value)}
            step={getStep()}
            min="0"
            required
            disabled={disabled}
          />
          {isDebugMode && 
            <span style={{ color: 'red', marginLeft: '5px', fontWeight: 'bold' }}>
              {getDebugId(`${formType}-width`)}
            </span>
          }
        </div>
        <div className="input-field">
          <label>
            Length: <span className="unit-label">(in)</span>
          </label>
          <input
            type="number"
            value={length}
            onChange={(e) => onChange('length', e.target.value)}
            onBlur={(e) => handleBlur('length', e.target.value)}
            step={getStep()}
            min="0"
            required
            disabled={disabled}
          />
          {isDebugMode && 
            <span style={{ color: 'red', marginLeft: '5px', fontWeight: 'bold' }}>
              {getDebugId(`${formType}-length`)}
            </span>
          }
        </div>
        {weight !== undefined && (
          <>
            <div className="input-field">
              <label>
                Weight without Icing: <span className="weight-unit">(g)</span>
              </label>
              <input
                type="number"
                value={weightNoIcing}
                onChange={(e) => onChange('weightNoIcing', e.target.value)}
                onBlur={(e) => handleBlur('weightNoIcing', e.target.value)}
                step={getWeightStep()}
                min="0"
                required
                disabled={disabled}
              />
              {isDebugMode && 
                <span style={{ color: 'red', marginLeft: '5px', fontWeight: 'bold' }}>
                  {getDebugId('ref-weightNoIcing')}
                </span>
              }
            </div>
            <div className="input-field">
              <label>
                Weight with Icing: <span className="weight-unit">(g)</span>
              </label>
              <input
                type="number"
                value={weight}
                onChange={(e) => onChange('weight', e.target.value)}
                onBlur={(e) => handleBlur('weight', e.target.value)}
                step={getWeightStep()}
                min="0"
                required
                disabled={disabled}
              />
              {isDebugMode && 
                <span style={{ color: 'red', marginLeft: '5px', fontWeight: 'bold' }}>
                  {getDebugId('ref-weight')}
                </span>
              }
            </div>
            <div className="input-field">
              <label>
                Icing Used: <span className="weight-unit">(g)</span>
              </label>

              <input
                type="number"
                value={(() => {
                  // Only calculate if both weights have valid values
                  if (!weight || !weightNoIcing || weight === '0') return '';
                  const w = parseFloat(weight);
                  const wNoIcing = parseFloat(weightNoIcing);
                  if (isNaN(w) || isNaN(wNoIcing)) return '';
                  return Math.max(0, (w - wNoIcing)).toFixed(2);
                })()}
                readOnly
                disabled
              />
              {/* Read-only fields don't get a debug ID */}
            </div>
            <div className="input-field">
              <label>
                Icing per Square Inch: <span className="weight-unit">(g)</span>
              </label>

              <input
                type="number"
                value={(() => {
                  // Only calculate if we have all required values
                  if (!width || !length || !weight || weight === '0') return '';
                  
                  const w = parseFloat(width);
                  const l = parseFloat(length);
                  const weightVal = parseFloat(weight);
                  const weightNoIcingVal = parseFloat(weightNoIcing || '0');
                  
                  // Validate all values
                  if (isNaN(w) || isNaN(l) || isNaN(weightVal) || isNaN(weightNoIcingVal)) return '';
                  
                  const area = w * l;
                  if (area <= 0) return '';
                  
                  // Ensure we don't have negative icing weight
                  const icingUsed = Math.max(0, weightVal - weightNoIcingVal);
                  
                  const perArea = (icingUsed / area).toFixed(4);
                  return perArea;
                })()}
                readOnly
                disabled
              />
              {/* Read-only fields don't get a debug ID */}
            </div>
          </>
        )}
        {count !== undefined && (
          <div className="input-field">
            <label>Number of Cookies:</label>
            <input
              type="number"
              value={count}
              onChange={(e) => onChange('count', e.target.value)}
              onBlur={(e) => handleBlur('count', e.target.value)}
              min="1"
              step="1"
              required
              disabled={disabled}
            />
            {isDebugMode && 
              <span style={{ color: 'red', marginLeft: '5px', fontWeight: 'bold' }}>
                {getDebugId('new-count')}
              </span>
            }
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieForm;
